import { useParams, Link } from 'react-router-dom';
import { useClinic, useWaitTime } from '../hooks/useApi';
import { AddressDisplay } from '../components/AddressDisplay';
import { ChevronRight, Phone, Clock, Users, Calendar } from 'lucide-react';

export default function ClinicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: clinic, isLoading, error } = useClinic(slug || '');
  const { data: waitTime } = useWaitTime(clinic?.id || '');

  if (isLoading) {
    return (
      <div role="status" aria-busy="true" aria-label="Loading clinic details" className="max-w-4xl mx-auto px-4 py-8">
        <div className="skeleton h-4 w-48 mb-6" />
        <div className="card mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
            <div className="flex-1">
              <div className="skeleton h-8 w-3/4 mb-3" />
              <div className="skeleton h-4 w-full mb-1" />
              <div className="skeleton h-4 w-1/2" />
            </div>
            <div className="skeleton h-10 w-40 rounded-full" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
            <div className="skeleton h-20 rounded-lg" />
            <div className="skeleton h-20 rounded-lg" />
            <div className="skeleton h-20 rounded-lg" />
          </div>
        </div>
        <div className="card">
          <div className="skeleton h-6 w-40 mb-4" />
          <div className="grid md:grid-cols-2 gap-3">
            <div className="skeleton h-16 rounded-lg" />
            <div className="skeleton h-16 rounded-lg" />
            <div className="skeleton h-16 rounded-lg" />
            <div className="skeleton h-16 rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !clinic) {
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-bold text-navy mb-4">Clinic Not Found</h2>
        <Link to="/clinics" className="btn btn-primary">Back to Clinics</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/">Home</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <Link to="/clinics">Find Clinics</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-navy font-medium truncate">{clinic.name}</span>
      </div>

      {/* Header */}
      <div className="card mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-extrabold text-navy">{clinic.name}</h1>
            <div className="mt-3">
              <AddressDisplay
                addressDisplay={clinic.address_display}
                address={clinic.address}
                island={clinic.island}
                plusCode={clinic.plus_code}
              />
            </div>
          </div>
          <Link to={`/book/${clinic.slug}`} className="btn btn-primary flex-shrink-0">
            <Calendar className="w-4 h-4" />
            Book Appointment
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
          {clinic.phone && (
            <div className="bg-cream rounded-lg p-4">
              <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
                <Phone className="w-3.5 h-3.5" /> Phone
              </div>
              <a href={`tel:${clinic.phone}`} className="text-primary font-semibold">
                {clinic.phone}
              </a>
            </div>
          )}
          <div className="bg-cream rounded-lg p-4">
            <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
              <Users className="w-3.5 h-3.5" /> Walk-ins
            </div>
            <p className="font-semibold text-navy">
              {clinic.accepts_walkins ? 'Accepted' : 'By appointment only'}
            </p>
          </div>
          {waitTime && (
            <div className="bg-cream rounded-lg p-4">
              <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
                <Clock className="w-3.5 h-3.5" /> Current Wait
              </div>
              <p className="font-semibold text-navy">
                {waitTime.average_minutes !== null ? `~${waitTime.average_minutes} min` : 'Unknown'}
              </p>
              <p className="text-xs text-gray-400">{waitTime.report_count} reports</p>
            </div>
          )}
        </div>
      </div>

      {/* Opening Hours */}
      {clinic.opening_hours && Object.keys(clinic.opening_hours).length > 0 && (
        <div className="card mb-6">
          <h2 className="text-lg font-bold text-navy mb-4">Opening Hours</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(clinic.opening_hours).map(([day, hours]) => (
              <div key={day}>
                <p className="font-medium text-navy capitalize text-sm">{day}</p>
                <p className="text-gray-500 text-sm">{hours || 'Closed'}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Services */}
      {clinic.services && clinic.services.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-bold text-navy mb-4">Services Offered</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {clinic.services.map((service) => (
              <div key={service.id} className="flex items-start gap-3 p-3 bg-cream rounded-lg">
                <span className="w-8 h-8 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-primary font-bold text-sm">+</span>
                </span>
                <div>
                  <p className="font-medium text-navy text-sm">{service.name}</p>
                  <p className="text-xs text-gray-400">{service.category}</p>
                  {service.notes && (
                    <p className="text-xs text-primary mt-1">{service.notes}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
